import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ClipboardList, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TripCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    location: '',
    trip_date: '',
    departure_time: '',
    return_time: '',
    description: '',
    fee_per_student: '',
    status: 'Planned',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/trips`, form);
      toast.success('Trip created successfully');
      navigate('/trips');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create trip');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-8">
        <Link to="/trips">
          <Button variant="outline" size="sm">
            <ArrowLeft size={16} className="mr-1" />
            Back
          </Button>
        </Link>
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
            <ClipboardList className="text-pink-500" size={32} />
            Create New Trip
          </h1>
          <p className="text-slate-500 mt-1">Set up a new school trip</p>
        </div>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="font-heading">Trip Details</CardTitle>
          <CardDescription>Fill in the information for the new trip</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input name="title" value={form.title} onChange={handleChange} required placeholder="e.g. Science Museum Visit" />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input name="location" value={form.location} onChange={handleChange} required placeholder="e.g. Harare" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Trip Date</Label>
                <Input name="trip_date" type="date" value={form.trip_date} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label>Departure Time</Label>
                <Input name="departure_time" type="time" value={form.departure_time} onChange={handleChange} required />
              </div>
              <div className="space-y-2">
                <Label>Return Time</Label>
                <Input name="return_time" type="time" value={form.return_time} onChange={handleChange} required />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Trip details and instructions..."
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fee per Student ($)</Label>
                <Input name="fee_per_student" type="number" step="0.01" value={form.fee_per_student} onChange={handleChange} required placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Planned">Planned</SelectItem>
                    <SelectItem value="Ongoing">Ongoing</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Link to="/trips">
                <Button type="button" variant="outline">Cancel</Button>
              </Link>
              <Button type="submit" disabled={loading} className="bg-pink-500 hover:bg-pink-600 text-white">
                {loading ? 'Creating...' : 'Create Trip'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
